import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/dropdown-menu";
import {
  useExperimentAnalysisRuns,
  useExperimentAnalysisRunStatus,
  useExperimentCampaigns,
  useScheduleExperimentAnalysisRun,
} from "@/http/api-hooks";
import { Experiment, ExperimentAnalysisRun, ExperimentType } from "@/http/api-types";
import { formatUTCDate, formatUTCDateTime } from "@/lib/utils";
import { ChevronRight, ExternalLink, Loader2, MoreHorizontal, Play, RotateCw } from "lucide-react";
import { useEffect, useState } from "react";
import { AnalysisResultSection } from "./AnalysisResultSection";
import { isExperimentAnalysisResultArtifact } from "./analysis-result";
import {
  formatRunDuration,
  getActiveExperimentAnalysisRun,
  getExperimentAnalysisDataWindowReason,
  getLatestExperimentAnalysisRunWithArtifact,
  getPrefectRunHref,
  getRunResourceHref,
  mergeExperimentAnalysisRuns,
} from "./results-tab";

type ResultsTabProps = {
  experiment: Experiment;
};

export function ResultsTab({ experiment }: ResultsTabProps) {
  const [flowRunId, setFlowRunId] = useState<string | null>(null);
  const [pendingAnalysisFlowRunId, setPendingAnalysisFlowRunId] = useState<string | null>(null);
  const { data: campaigns } = useExperimentCampaigns(experiment.id);
  const { data: persistedRuns, mutate: mutatePersistedRuns } = useExperimentAnalysisRuns(experiment.id, 10);
  const {
    trigger: scheduleRun,
    isMutating: isScheduling,
    data: scheduledRun,
  } = useScheduleExperimentAnalysisRun(experiment.id);
  const {
    data: polledRun,
    error: pollingError,
    isValidating,
    mutate: refreshRun,
  } = useExperimentAnalysisRunStatus(experiment.id, flowRunId);

  useEffect(() => {
    if (!flowRunId && persistedRuns?.[0]?.flow_run_id) {
      setFlowRunId(persistedRuns[0].flow_run_id);
    }
  }, [flowRunId, persistedRuns]);

  const selectedPersistedRun = persistedRuns?.find(run => run.flow_run_id === flowRunId);
  const selectedRun =
    polledRun?.data ||
    (scheduledRun?.data?.flow_run_id === flowRunId ? scheduledRun.data : undefined) ||
    selectedPersistedRun;
  const recentRuns = mergeExperimentAnalysisRuns(selectedRun, persistedRuns || []);
  const latestRun = recentRuns[0];
  const latestRunWithArtifact = getLatestExperimentAnalysisRunWithArtifact(recentRuns);
  // Show the selected run whenever it carries any artifact payload — even an older/unrecognized shape, so
  // AnalysisResultSection can prompt a re-run. Otherwise fall back to the latest run with a valid artifact.
  const selectedRunHasArtifactPayload = Boolean(
    selectedRun?.result_artifact && typeof selectedRun.result_artifact === "object",
  );
  const displayedAnalysisRun = selectedRunHasArtifactPayload ? selectedRun : latestRunWithArtifact;
  const scheduleReadiness = getScheduleReadiness(experiment, campaigns || experiment.campaigns || []);

  useEffect(() => {
    if (!pendingAnalysisFlowRunId) {
      return;
    }

    const pendingRun = recentRuns.find(run => run.flow_run_id === pendingAnalysisFlowRunId);
    if (!pendingRun || !isExperimentAnalysisResultArtifact(pendingRun.result_artifact)) {
      return;
    }

    setPendingAnalysisFlowRunId(null);
    void import("sonner").then(({ toast }) => {
      toast.success("Latest analysis results are now shown");
    });
  }, [pendingAnalysisFlowRunId, recentRuns]);

  const handleSchedule = async () => {
    try {
      const response = await scheduleRun({ excluded_dates: [], overwrite_artifacts: false });
      const nextFlowRunId = response.data.flow_run_id;
      setPendingAnalysisFlowRunId(displayedAnalysisRun ? nextFlowRunId : null);
      setFlowRunId(nextFlowRunId);
      await mutatePersistedRuns();
      const { toast } = await import("sonner");
      toast.success("Analysis run scheduled");
    } catch (err) {
      const { toast } = await import("sonner");
      toast.error("Failed to schedule analysis run", { description: errorMessage(err) });
    }
  };

  return (
    <div className="space-y-4">
      <AnalysisRunPanel
        run={selectedRun}
        latestRun={latestRun}
        runs={recentRuns}
        canSchedule={scheduleReadiness.canSchedule}
        scheduleDisabledReason={scheduleReadiness.reason}
        isScheduling={isScheduling}
        isPolling={isValidating}
        pollingError={pollingError}
        onSchedule={handleSchedule}
        onRefresh={async () => {
          await Promise.all([refreshRun(), mutatePersistedRuns()]);
        }}
        onSelectRun={nextFlowRunId => setFlowRunId(nextFlowRunId)}
      />

      {displayedAnalysisRun ? (
        <AnalysisResultSection key={displayedAnalysisRun.flow_run_id} run={displayedAnalysisRun} />
      ) : null}
    </div>
  );
}

type ScheduleReadiness = {
  canSchedule: boolean;
  reason?: string;
};

type AnalysisMember = {
  variant: string;
};

type AnalysisRunPanelProps = {
  run?: ExperimentAnalysisRun;
  latestRun?: ExperimentAnalysisRun;
  runs: ExperimentAnalysisRun[];
  canSchedule: boolean;
  scheduleDisabledReason?: string;
  isScheduling: boolean;
  isPolling: boolean;
  pollingError: unknown;
  onSchedule: () => void;
  onRefresh: () => Promise<unknown>;
  onSelectRun: (flowRunId: string) => void;
};

function AnalysisRunPanel({
  run,
  latestRun,
  runs,
  canSchedule,
  scheduleDisabledReason,
  isScheduling,
  isPolling,
  pollingError,
  onSchedule,
  onRefresh,
  onSelectRun,
}: AnalysisRunPanelProps) {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const activeRun = getActiveExperimentAnalysisRun(runs);
  const scheduleDisabled = isScheduling || Boolean(activeRun) || !canSchedule;
  const scheduleLabel = "Schedule analysis";
  const isLatestSelected = Boolean(run && latestRun && run.flow_run_id === latestRun.flow_run_id);
  const runOriginLabel = isLatestSelected ? "Latest" : "History";
  const activeRunIsDifferent = Boolean(activeRun && run && activeRun.flow_run_id !== run.flow_run_id);
  const resolvedScheduleDisabledReason = !canSchedule
    ? scheduleDisabledReason
    : activeRun
      ? activeRunIsDifferent
        ? "Another analysis run is already in progress. Switch back to the latest run to follow it in Prefect."
        : "An analysis run is already in progress."
      : undefined;
  const startedAtLabel = run?.start_time ? "Started" : run?.expected_start_time ? "Expected start" : null;
  const startedAtValue = run?.start_time
    ? formatUTCDateTime(run.start_time)
    : run?.expected_start_time
      ? formatUTCDateTime(run.expected_start_time)
      : null;
  const lastEventLabel = run?.end_time ? "Ended" : run?.state_timestamp ? "Last update" : null;
  const lastEventValue = run?.end_time
    ? formatUTCDateTime(run.end_time)
    : run?.state_timestamp
      ? formatUTCDateTime(run.state_timestamp)
      : null;
  const summaryRunDate = run?.start_time || run?.expected_start_time || run?.created;
  const formattedDuration = formatRunDuration(run?.total_run_time);
  const prefectRunHref = getPrefectRunHref(run?.flow_run_id, run?.prefect_ui_url);
  const artifactHref = getRunResourceHref(run?.result_path);

  return (
    <section className="rounded-md border bg-background p-4">
      <Collapsible open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0 flex-1 space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-semibold tracking-tight">Analysis Runs</h3>
              {isPolling ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
              {run ? (
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-7 gap-1.5 px-2 text-muted-foreground">
                    <ChevronRight
                      className={`h-4 w-4 transition-transform duration-200 ${isDetailsOpen ? "rotate-90" : ""}`}
                    />
                    {isDetailsOpen ? "Hide details" : "Show details"}
                  </Button>
                </CollapsibleTrigger>
              ) : null}
            </div>

            {run ? (
              <>
                <div className="rounded-md border bg-muted/20 p-3">
                  {isDetailsOpen ? (
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            Selected run
                          </div>
                          <Badge variant="outline" className={analysisStateClassName(run.state_type)}>
                            {run.state_name}
                          </Badge>
                          <Badge variant="outline" className="border-border bg-muted/30 text-muted-foreground">
                            {runOriginLabel}
                          </Badge>
                        </div>
                        <div className="break-all font-mono text-sm text-foreground">{run.flow_run_id}</div>
                        {run.name ? <div className="text-sm text-muted-foreground">{run.name}</div> : null}
                      </div>

                      {prefectRunHref || artifactHref ? (
                        <div className="flex shrink-0 flex-wrap items-center gap-2">
                          {prefectRunHref ? <RunResourceLink label="Pipeline Run" href={prefectRunHref} /> : null}
                          {artifactHref ? <RunResourceLink label="Raw Analysis Results" href={artifactHref} /> : null}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="outline" className={analysisStateClassName(run.state_type)}>
                        {run.state_name}
                      </Badge>
                      {summaryRunDate ? (
                        <span className="text-sm text-muted-foreground">{formatUTCDate(summaryRunDate)}</span>
                      ) : null}
                    </div>
                  )}
                </div>

                <CollapsibleContent className="space-y-4">
                  <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                    <RunMetadataItem label="Created" value={run.created ? formatUTCDateTime(run.created) : null} />
                    <RunMetadataItem label={startedAtLabel} value={startedAtValue} />
                    <RunMetadataItem label={lastEventLabel} value={lastEventValue} />
                    <RunMetadataItem label="Duration" value={formattedDuration} />
                  </div>

                  {run.state_message ? (
                    <div className="rounded-md border bg-muted/20 p-3">
                      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Status message
                      </div>
                      <p className="mt-1 break-words text-sm text-foreground">{run.state_message}</p>
                    </div>
                  ) : null}
                </CollapsibleContent>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">No analysis run scheduled yet.</p>
            )}
          </div>

          <div className="flex w-full justify-end gap-2 xl:w-auto xl:shrink-0">
            {run || runs.length > 0 ? (
              <AnalysisRunActionsMenu
                run={run}
                runs={runs}
                latestRun={latestRun}
                selectedFlowRunId={run?.flow_run_id}
                isLatestSelected={isLatestSelected}
                isPolling={isPolling}
                onRefresh={onRefresh}
                onSelectRun={onSelectRun}
              />
            ) : null}

            <Button size="sm" onClick={onSchedule} disabled={scheduleDisabled} title={resolvedScheduleDisabledReason}>
              {isScheduling ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
              {isScheduling ? "Scheduling" : scheduleLabel}
            </Button>
          </div>
        </div>
      </Collapsible>

      {resolvedScheduleDisabledReason ? (
        <div className="mt-3 text-sm text-muted-foreground">{resolvedScheduleDisabledReason}</div>
      ) : null}

      {pollingError ? (
        <div className="mt-3 text-sm text-destructive">Failed to refresh analysis run status.</div>
      ) : null}
    </section>
  );
}

type RunMetadataItemProps = {
  label: string | null;
  value: string | null;
};

function RunMetadataItem({ label, value }: RunMetadataItemProps) {
  if (!label || !value) {
    return null;
  }

  return (
    <div className="rounded-md border bg-muted/10 px-3 py-2">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm text-foreground">{value}</div>
    </div>
  );
}

type RunResourceLinkProps = {
  label: string;
  href: string;
};

function RunResourceLink({ label, href }: RunResourceLinkProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 rounded-md border bg-background px-2.5 py-1 text-sm text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
    >
      <span>{label}</span>
      <ExternalLink className="h-3.5 w-3.5 shrink-0" />
    </a>
  );
}

type AnalysisRunActionsMenuProps = {
  run?: ExperimentAnalysisRun;
  runs: ExperimentAnalysisRun[];
  latestRun?: ExperimentAnalysisRun;
  selectedFlowRunId?: string;
  isLatestSelected: boolean;
  isPolling: boolean;
  onRefresh: () => Promise<unknown>;
  onSelectRun: (flowRunId: string) => void;
};

function AnalysisRunActionsMenu({
  run,
  runs,
  latestRun,
  selectedFlowRunId,
  isLatestSelected,
  isPolling,
  onRefresh,
  onSelectRun,
}: AnalysisRunActionsMenuProps) {
  const prefectRunHref = getPrefectRunHref(run?.flow_run_id, run?.prefect_ui_url);
  const openPrefect = () => {
    if (!prefectRunHref) {
      return;
    }

    window.open(prefectRunHref, "_blank", "noopener,noreferrer");
  };
  const artifactHref = getRunResourceHref(run?.result_path);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Run actions">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel>Run actions</DropdownMenuLabel>

        {prefectRunHref ? (
          <DropdownMenuItem onSelect={openPrefect}>
            Open in Prefect UI
            <DropdownMenuShortcut>
              <ExternalLink className="h-3.5 w-3.5" />
            </DropdownMenuShortcut>
          </DropdownMenuItem>
        ) : null}

        {artifactHref ? (
          <DropdownMenuItem onSelect={() => window.open(artifactHref, "_blank", "noopener,noreferrer")}>
            Open raw analysis results
            <DropdownMenuShortcut>
              <ExternalLink className="h-3.5 w-3.5" />
            </DropdownMenuShortcut>
          </DropdownMenuItem>
        ) : null}

        {run ? (
          <DropdownMenuItem
            disabled={isPolling}
            onSelect={() => {
              void onRefresh();
            }}
          >
            Refresh selected run
            <DropdownMenuShortcut>
              {isPolling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
            </DropdownMenuShortcut>
          </DropdownMenuItem>
        ) : null}

        {!isLatestSelected && latestRun ? (
          <DropdownMenuItem onSelect={() => onSelectRun(latestRun.flow_run_id)}>View latest run</DropdownMenuItem>
        ) : null}

        {runs.length > 0 ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Recent runs</DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-80">
                <DropdownMenuLabel>Choose a run</DropdownMenuLabel>
                {runs.map(historyRun => {
                  const isSelected = historyRun.flow_run_id === selectedFlowRunId;
                  const isLatest = historyRun.flow_run_id === latestRun?.flow_run_id;
                  const durationLabel = formatRunDuration(historyRun.total_run_time);
                  const runMeta = [
                    historyRun.state_name || historyRun.state_type,
                    historyRun.created ? formatUTCDateTime(historyRun.created) : null,
                    durationLabel,
                  ]
                    .filter(Boolean)
                    .join(" • ");

                  return (
                    <DropdownMenuItem
                      key={historyRun.flow_run_id}
                      onSelect={() => onSelectRun(historyRun.flow_run_id)}
                      className="flex-col items-start gap-1"
                    >
                      <div className="flex w-full items-center gap-2">
                        <span className="truncate font-mono text-xs text-foreground">{historyRun.flow_run_id}</span>
                        <DropdownMenuShortcut>
                          {isSelected ? "Selected" : isLatest ? "Latest" : ""}
                        </DropdownMenuShortcut>
                      </div>
                      <span className="line-clamp-2 text-xs text-muted-foreground">{runMeta}</span>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function getScheduleReadiness(experiment: Experiment, customMembers: AnalysisMember[]): ScheduleReadiness {
  if (!experiment.start_date || !experiment.end_date) {
    return { canSchedule: false, reason: "Experiment dates are required before analysis can run." };
  }

  const members = experiment.type === ExperimentType.Custom ? customMembers : experiment.prediction_flights || [];
  const controlCount = members.filter(member => member.variant === "control").length;
  const testCount = members.filter(member => member.variant === "test").length;
  const memberLabel = experiment.type === ExperimentType.Custom ? "campaign" : "prediction flight";

  // Custom experiments may pair several control campaigns with their tests; global experiments
  // still require exactly one control flight.
  if (experiment.type === ExperimentType.Custom) {
    if (controlCount === 0) {
      return { canSchedule: false, reason: `Analysis requires at least one control ${memberLabel}.` };
    }
  } else if (controlCount !== 1) {
    return { canSchedule: false, reason: `Analysis requires exactly one control ${memberLabel}.` };
  }
  if (testCount === 0) {
    return { canSchedule: false, reason: `Analysis requires at least one test ${memberLabel}.` };
  }

  const dataWindowReason = getExperimentAnalysisDataWindowReason(experiment.start_date, experiment.end_date);
  if (dataWindowReason) {
    return { canSchedule: false, reason: dataWindowReason };
  }

  return { canSchedule: true };
}

function analysisStateClassName(stateType: string) {
  switch (stateType.toUpperCase()) {
    case "COMPLETED":
      return "border-green-200 bg-green-50 text-green-700";
    case "RUNNING":
      return "border-blue-200 bg-blue-50 text-blue-700";
    case "FAILED":
    case "CRASHED":
      return "border-red-200 bg-red-50 text-red-700";
    case "CANCELLED":
      return "border-gray-200 bg-gray-50 text-gray-700";
    default:
      return "border-amber-200 bg-amber-50 text-amber-700";
  }
}

function errorMessage(err: unknown) {
  if (typeof err === "object" && err && "response" in err) {
    const response = (err as { response?: { data?: { error?: string } } }).response;
    if (response?.data?.error) return response.data.error;
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}
